import React, { useState, useMemo } from "react";
import { Alert, Button, Col, Modal, Row } from "react-bootstrap";
import { Link } from "react-router-dom";
import { PacmanLoader } from "react-spinners";
import { GenericTable } from "components/GenericTable";
import { createElementIdFactory, createReusableTableElementIds } from "utils/elementIds";
import "containers/common/List.css";
import "containers/common/Table.css";

import {
  createColumnHelper,
} from "@tanstack/react-table";


function HeadOfficePageList({
  isAuthenticated,
  data,
  isLoading,
  leaseid,
  user,
  updateTableState,
  saveRecord,
  toggleHeadOfficeSearchDialog,
  controlType,
}) {

  const [errorMessage, setErrorMessage] = useState("");
  const [showAlert, setShowAlert] = useState(null);

  // --- TranStack Table Setup ---
  const tableData = data || [];
  const columnHelper = createColumnHelper();
  const createId = useMemo(() => createElementIdFactory("head-office-list"), []);
  const tableElementIds = useMemo(() => createReusableTableElementIds(createId), [createId]);

  const col_led_head_office_id = columnHelper.accessor("led_head_office_id", {
    id: "led_head_office_id",
    header: "Head Office ID",
    cell: (e) => (
      <Link
        target="_blank"
        rel="noopener noreferrer"
        to={{
          pathname: `../headoffices/${e.getValue()}`,
          state: {
            data: tableData
              .filter((a) => a.selected)
              .map((a) => a.led_head_office_id),
          },
        }}
      >
        {e.getValue()}
      </Link>
    ),
    size: 60,
    enableSorting: false,
  });
  const col_tenant_name = columnHelper.accessor("tenant_name", {
    id: "tenant_name",
    header: "Name",
    cell: (info) => info.getValue(),
    enableSorting: false,
  });
  const col_address_line_1 = columnHelper.accessor("address_line_1", {
    id: "address_line_1",
    header: "Address Line 1",
    cell: (info) => info.getValue(),
    enableSorting: false,
  });
  const col_address_line_2 = columnHelper.accessor("address_line_2", {
    id: "address_line_2",
    header: "Address Line 2",
    cell: (info) => info.getValue(),
    size: 120,
    enableSorting: false,
  });
  const col_suburb = columnHelper.accessor("suburb", {
    id: "suburb",
    header: "Suburb",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: false,
  });
  const col_state = columnHelper.accessor("state", {
    id: "state",
    header: "State",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: false,
  });
  const col_postcode = columnHelper.accessor("postcode", {
    id: "postcode",
    header: "Postcode",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: false,
  });
  const col_email = columnHelper.accessor("email", {
    id: "email",
    header: "Email",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: false,
  });
  const col_telephone = columnHelper.accessor("telephone", {
    id: "telephone",
    header: "Telephone",
    cell: (info) => info.getValue(),
    size: 60,
    enableSorting: false,
  });

  const columns = [
    col_led_head_office_id,
    col_tenant_name,
    col_address_line_1,
    col_address_line_2,
    col_suburb,
    col_state,
    col_postcode,
    col_email,
    col_telephone,
  ];

  const columns2 = [
    col_led_head_office_id,
    col_tenant_name,
    col_address_line_1,
    col_address_line_2,
    col_suburb,
    col_state,
    col_postcode,
    col_email,
    col_telephone,
    columnHelper.accessor("head_office_id", {
      id: "head_office_id",
      header: "",
      cell: (info) => (
        <Button
          type="button"
          size="sm"
          variant="primary"
          onClick={() => handleAddHeadOfficeLease(info.row.original)}
        >
          Link to Lease
        </Button>
      ),
      size: 60,
    }),
  ];

  const columnsMap = {
    table2: columns2,
    table: columns,
  };

  // --- Data Fetching and Update Functions ---

  async function handleAddHeadOfficeLease(headOffice) {
    let localData = [...data];

    let headOfficeLease = {
      led_head_office_id: headOffice.led_head_office_id,
      lease_id: leaseid,

      created_by: user,
      last_edited_by: user,
      new_record: true,
    };
    localData.unshift(headOfficeLease);
    updateTableState(localData, "headofficeleases");
    const result = await saveRecord(
      headOfficeLease,
      "headOfficeLeases",
      "led_head_office_lease_id"
    );

    if (!result.result) {
      setErrorMessage(result.error_message);
      setShowAlert(true);
    } else {
      toggleHeadOfficeSearchDialog();
    }
  }


  // --- Render Functions ---
  function renderHeadOffices() {
    const activeColumns = columnsMap[controlType] || columnsMap.table;

    return (
      <div className="componentRendering">
        <Modal id={createId("error-modal")} show={showAlert} onHide={(e) => { setShowAlert(!showAlert) }} centered >
          <Modal.Header id={createId("error-modal-header")} closeButton> <Modal.Title>Uh Oh!</Modal.Title></Modal.Header>
          <Modal.Body id={createId("error-modal-body")} > <Alert variant="danger"  >{errorMessage}</Alert> </Modal.Body>
        </Modal>
        <Row>
          <Col className="text-right">
            <Link to={`/headoffices/new`}>
              <Button id={createId("add-btn")} variant="primary">Add</Button>
            </Link>
          </Col>
        </Row>

        {/* Record Count */}
        {controlType === "table" && (
          <Row className="mb-2">
            <Col><b>{(data || []).length} of {data?.[0]?.result_count || "0"} records returned.</b></Col>
          </Row>
        )}

        <Row className="show-Container">
          <Col sm={12} md={12} lg={12}>
            {isLoading ? (
              <div className="outer-div">
                <div className="sweet-loading inner-div">
                  <PacmanLoader
                    cssOverride={{ display: "block", margin: "0 auto" }}
                    size={20}
                    color={"#e03c31"}
                    loading={isLoading}
                  />
                </div>
              </div>
            ) : (
              <GenericTable
                columns={activeColumns}
                data={tableData}
                section="head-office-list"
                pageSize={50}
                elementIds={tableElementIds}
              />
            )}
          </Col>
        </Row>
      </div>
    );
  }
  return (
    <div className="HeadOfficeList">
      {isAuthenticated ? renderHeadOffices() : null}
    </div>
  );
}

export default HeadOfficePageList; 